import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Modal,
  Text,
  Select,
  Button,
  Flex,
  CardSelector,
  PriceDisplay,
} from '@heartinternet/hi-ui';
import type { CardSelectorOption } from '@heartinternet/hi-ui';
import './PremiumUpsellModal.css';

const PRODUCTS_ENDPOINT = '/cp/rpc/premium_hosting/products';
const BASKET_ENDPOINT = '/manage/basket.cgi';

type PackageKey = 'base' | 'plus' | 'pro';
type Interval = 'monthly' | 'yearly';
type PaymentMethod = 'card' | 'debit';

interface PackagePrice {
  monthlyNow: number;
  monthlyThen: number;
  yearlyNow: number;
  yearlyThen: number;
}

interface PackageMeta {
  visits: string;
  ram: number;
  disk: string;
}

interface PackageInfo {
  product: string;
  title: string;
  price: PackagePrice;
  text: string;
  meta: PackageMeta;
}

/**
 * Hardcoded package data. Mirrors `modalContent` in the legacy
 * `javascript/cp-premium-hosting-upsell.js`. Only the "now" prices are ever
 * overwritten (best-effort) from the products RPC; "then" prices stay fixed.
 */
const PACKAGES: Record<PackageKey, PackageInfo> = {
  base: {
    product: 'managed-web-server.s',
    title: 'Base',
    price: { monthlyNow: 29.99, monthlyThen: 49.99, yearlyNow: 329.89, yearlyThen: 549.89 },
    text: 'Perfect for high-traffic WordPress blogs, multiple small websites, or busy message boards.',
    meta: { visits: '375,000', ram: 4, disk: '50GB' },
  },
  plus: {
    product: 'managed-web-server.m',
    title: 'Plus',
    price: { monthlyNow: 49.99, monthlyThen: 69.99, yearlyNow: 549.89, yearlyThen: 769.89 },
    text: 'Created for large Magento e-commerce shops, data intensive mobile applications, or high-profile blogging networks.',
    meta: { visits: '750,000', ram: 8, disk: 'Unlimited' },
  },
  pro: {
    product: 'managed-web-server.l',
    title: 'Pro',
    price: { monthlyNow: 119.99, monthlyThen: 129.99, yearlyNow: 1319.89, yearlyThen: 1429.89 },
    text: 'Powerful server hosting for multiple high-traffic online retailers, mission-critical applications, or high-powered online communities.',
    meta: { visits: '1,500,000', ram: 16, disk: 'Unlimited' },
  },
};

const PACKAGE_KEYS: PackageKey[] = ['base', 'plus', 'pro'];

/** Shape of the `/cp/rpc/premium_hosting/products` payload we care about. */
interface ProductsResponse {
  products?: Record<
    string,
    Array<{
      payment_interval?: string;
      price?: { card?: number };
    }>
  >;
}

/** Runtime "now" prices, keyed by package. Seeded from the hardcoded data. */
type NowPrices = Record<PackageKey, { monthlyNow: number; yearlyNow: number }>;

function initialNowPrices(): NowPrices {
  return {
    base: { monthlyNow: PACKAGES.base.price.monthlyNow, yearlyNow: PACKAGES.base.price.yearlyNow },
    plus: { monthlyNow: PACKAGES.plus.price.monthlyNow, yearlyNow: PACKAGES.plus.price.yearlyNow },
    pro: { monthlyNow: PACKAGES.pro.price.monthlyNow, yearlyNow: PACKAGES.pro.price.yearlyNow },
  };
}

/** Look up a package key from its product name (e.g. `managed-web-server.s`). */
function keyForProduct(product: string): PackageKey | undefined {
  return PACKAGE_KEYS.find((k) => PACKAGES[k].product === product);
}

export interface PremiumUpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PremiumUpsellModal: React.FC<PremiumUpsellModalProps> = ({ isOpen, onClose }) => {
  const [selectedKey, setSelectedKey] = useState<PackageKey>('base');
  const [interval, setInterval] = useState<Interval>('monthly');
  const [nowPrices, setNowPrices] = useState<NowPrices>(initialNowPrices);

  // Fetch the live "now" prices when the modal opens. Best-effort: any failure
  // silently leaves the hardcoded prices in place (matches legacy behaviour —
  // the jQuery version had no error handler on this GET).

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    fetch(PRODUCTS_ENDPOINT)
      .then((r) => r.json())
      .then((data: ProductsResponse) => {
        if (cancelled || !data.products) return;

        setNowPrices((prev) => {
          const next: NowPrices = {
            base: { ...prev.base },
            plus: { ...prev.plus },
            pro: { ...prev.pro },
          };
          Object.keys(data.products!).forEach((productName) => {
            const key = keyForProduct(productName);
            if (!key) return;
            data.products![productName].forEach((entry) => {
              const cardPrice = entry.price?.card;
              if (typeof cardPrice !== 'number') return;
              if (entry.payment_interval === 'monthly') {
                next[key].monthlyNow = cardPrice;
              } else if (entry.payment_interval === 'yearly') {
                next[key].yearlyNow = cardPrice;
              }
            });
          });
          return next;
        });
      })
      .catch(() => {
        /* best-effort: keep hardcoded prices */
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const pkg = PACKAGES[selectedKey];

  const nowPrice =
    interval === 'monthly' ? nowPrices[selectedKey].monthlyNow : nowPrices[selectedKey].yearlyNow;
  const thenPrice = interval === 'monthly' ? pkg.price.monthlyThen : pkg.price.yearlyThen;
  const priceInterval = interval === 'monthly' ? 'month' : 'year';

  const options = useMemo<CardSelectorOption[]>(
    () =>
      PACKAGE_KEYS.map((key) => {
        const p = PACKAGES[key];
        const now = interval === 'monthly' ? nowPrices[key].monthlyNow : nowPrices[key].yearlyNow;
        const then = interval === 'monthly' ? p.price.monthlyThen : p.price.yearlyThen;
        return {
          id: key,
          label: (
            <span className="hi-ui-ph-upsell__card-label">
              <span className="hi-ui-ph-upsell__card-title">{p.title}</span>
              <PriceDisplay amount={now} originalAmount={then} interval={priceInterval} size="sm" />
              <span className="hi-ui-ph-upsell__card-blurb">{p.text}</span>
            </span>
          ),
        };
      }),
    [interval, nowPrices, priceInterval]
  );

  const handlePackageChange = useCallback((value: string | string[]) => {
    const next = Array.isArray(value) ? value[0] : value;
    if (next === 'base' || next === 'plus' || next === 'pro') {
      setSelectedKey(next);
    }
  }, []);

  const handleClose = useCallback(() => {
    // Reset to the legacy default view for the next open.
    setSelectedKey('base');
    setInterval('monthly');
    onClose();
  }, [onClose]);

  const goToBasket = useCallback(
    (method: PaymentMethod) => {
      const type = pkg.product.split('managed-web-server.')[1];
      const basketAddCode = `premium_hosting.${type}:${interval}:${method}`;
      const params = new URLSearchParams({
        'submit-type': 'PURCHASE',
        type: 'MULTI',
        'basket-add-code': basketAddCode,
      });
      window.location.href = `${BASKET_ENDPOINT}?${params.toString()}`;
    },
    [pkg.product, interval]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Upgrade to premium hosting now!"
      size="large"
      className="hi-ui-ph-upsell"
      footer={
        <Text variant="caption" inline>
          Disclaimer: Premium Hosting does not replace your Reseller package
        </Text>
      }
    >
      <Flex direction="column" gap="md">
        <Text variant="body" inline>
          Select your Premium Hosting package:
        </Text>

        <CardSelector
          options={options}
          value={selectedKey}
          onChange={handlePackageChange}
          columns={3}
          legend="Premium Hosting package"
          legendHidden
        />

        <Flex direction="row" gap="lg" wrap className="hi-ui-ph-upsell__details">
          {/* Left column: package spec + key features */}
          <Flex direction="column" gap="md" flex="1 1 260px">
            <div>
              <Text variant="heading-4">Premium hosting</Text>
              <dl className="hi-ui-ph-upsell__spec">
                <dt>Estimated Monthly Visits</dt>
                <dd>{pkg.meta.visits}</dd>
                <dt>Number of Websites</dt>
                <dd>Unlimited</dd>
                <dt>Uptime SLA</dt>
                <dd>99.99%</dd>
                <dt>Managed OS &amp; Hardware</dt>
                <dd>Yes</dd>
                <dt>Dedicated RAM</dt>
                <dd>{pkg.meta.ram}GB</dd>
              </dl>
            </div>

            <div>
              <Text variant="heading-4">Unlimited key features</Text>
              <ul className="hi-ui-ph-upsell__features">
                <li>
                  <strong>1GB</strong> Database size
                </li>
                <li>
                  <strong>ISPA</strong> Award winning
                </li>
                <li>
                  <strong>{pkg.meta.ram}GB</strong> Dedicated RAM
                </li>
                <li>
                  <strong>{pkg.meta.disk}</strong> Webspace
                </li>
              </ul>
            </div>
          </Flex>

          {/* Right column: payment */}
          <Flex direction="column" gap="md" flex="1 1 260px">
            <div>
              <Text variant="heading-4">Payment</Text>
              <Text variant="body-small" inline>
                Payment interval:
              </Text>
              <Select
                value={interval}
                onChange={(e) => setInterval(e.target.value as Interval)}
                size="sm"
                fullWidth
                aria-label="Payment interval"
              >
                <option value="monthly">Monthly - £{nowPrices[selectedKey].monthlyNow}</option>
                <option value="yearly">Annually - £{nowPrices[selectedKey].yearlyNow}</option>
              </Select>
              <Text variant="caption" className="hi-ui-ph-upsell__smallprint">
                For first year - then £{thenPrice}
                {interval === 'monthly' ? '/mo' : '/yr'}
              </Text>
              <Text variant="caption" className="hi-ui-ph-upsell__smallprint">
                Pay yearly and get one month free
              </Text>
            </div>

            <div>
              <Text variant="heading-4">Payment method:</Text>
              <Flex direction="column" gap="sm">
                <Button variant="primary" size="small" onClick={() => goToBasket('card')}>
                  Upgrade by card
                </Button>
                <Button variant="secondary" size="small" onClick={() => goToBasket('debit')}>
                  Upgrade by direct debit
                </Button>
              </Flex>
            </div>
          </Flex>
        </Flex>

        <Text variant="caption" inline>
          Currently viewing: {pkg.title} — £{nowPrice}
          {interval === 'monthly' ? '/mo' : '/yr'} and then £{thenPrice}
          {interval === 'monthly' ? '/mo' : '/yr'}
        </Text>
      </Flex>
    </Modal>
  );
};
